import { useState, useEffect } from "react"
import { Box, Flex, Text, SimpleGrid, Icon, Spinner } from "@chakra-ui/react"
import { WarningIcon, TriangleDownIcon, TriangleUpIcon, InfoIcon } from "@chakra-ui/icons"
import axios from "axios"
import { neumorph } from "./neumorph"
import { usePeriod } from "./PeriodContext"
import { formatCurrency, formatNum } from "./KpiHeader"

const DORMANT_THRESHOLD = 10000 // R10k
const LOW_GP_THRESHOLD = 15 // %
const REVENUE_DROP_THRESHOLD = 10 // %

const AlertItem = ({ icon, label, value, severity = "warn", detail }) => {
  const accentColor =
    severity === "danger"
      ? neumorph.accentRed
      : severity === "good"
      ? neumorph.accentGreen
      : neumorph.accentOrange

  return (
    <Box sx={{ ...neumorph.raisedSm, p: 3, borderLeft: `4px solid ${accentColor}` }}>
      <Flex align="flex-start" gap={2}>
        <Icon as={icon} color={accentColor} boxSize={4} mt={0.5} />
        <Box flex="1">
          <Text fontSize="2xs" color={neumorph.textMuted} textTransform="uppercase" fontWeight="600" letterSpacing="0.05em">
            {label}
          </Text>
          <Text fontSize="sm" color={neumorph.textPrimary} fontWeight="700" mt={0.5}>
            {value}
          </Text>
          {detail && (
            <Text fontSize="2xs" color={neumorph.textMuted} mt={0.5}>
              {detail}
            </Text>
          )}
        </Box>
      </Flex>
    </Box>
  )
}

const AlertPanel = ({ supplier, retailer }) => {
  const { startDate, endDate } = usePeriod()
  const [loading, setLoading] = useState(false)
  const [alerts, setAlerts] = useState([])

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()

    const fetchAndCompute = async () => {
      setLoading(true)
      try {
        const userid = localStorage.getItem("userid")
        const isPnp = retailer.toUpperCase() === "PNP"

        // Parallel fetch — only what's needed for alert evaluation
        const requests = [
          axios.post(
            `${process.env.REACT_APP_API_URL}/insights/dormant-stock/kpi`,
            { supplier, retailer, userid, daysThreshold: 90 },
            { signal: controller.signal }
          ),
          axios.post(
            `${process.env.REACT_APP_API_URL}/insights/cost-sales`,
            { supplier, retailer, userid, startDate, endDate },
            { signal: controller.signal }
          ),
          axios.post(
            `${process.env.REACT_APP_API_URL}/insights/growth-comparison`,
            { supplier, retailer, userid },
            { signal: controller.signal }
          ),
        ]
        if (isPnp) {
          requests.push(
            axios.post(
              `${process.env.REACT_APP_API_URL}/insights/article-status`,
              { supplier, retailer, userid },
              { signal: controller.signal }
            )
          )
        }

        const results = await Promise.allSettled(requests)
        const [dormantRes, costRes, growthRes, statusRes] = results

        const triggered = []

        // Alert: dormant stock value > threshold
        if (dormantRes.status === "fulfilled" && dormantRes.value.data?.status === 1) {
          const d = dormantRes.value.data.data
          const value = parseFloat(d?.total_dormant_value || 0)
          if (value > DORMANT_THRESHOLD) {
            triggered.push({
              icon: WarningIcon,
              label: "Dormant capital",
              value: formatCurrency(value),
              detail: `${formatNum(d?.dormant_article_count)} articles, ${formatNum(d?.dormant_store_count)} stores. >90 days no sales.`,
              severity: "warn",
              key: "dormant",
            })
          }
        }

        // Alert: low GP articles
        if (costRes.status === "fulfilled" && costRes.value.data?.status === 1) {
          const items = costRes.value.data.data?.byArticle || []
          const lowGpItems = items.filter(
            (r) => r.has_cost && parseFloat(r.gp_percent) < LOW_GP_THRESHOLD
          )
          if (lowGpItems.length > 0) {
            triggered.push({
              icon: WarningIcon,
              label: "Low GP articles",
              value: `${lowGpItems.length} article${lowGpItems.length !== 1 ? "s" : ""} below ${LOW_GP_THRESHOLD}% GP`,
              detail: lowGpItems
                .slice(0, 3)
                .map((i) => i.articledescription?.slice(0, 25))
                .join(", "),
              severity: "danger",
              key: "low-gp",
            })
          }
        }

        // Alert: month-over-month revenue drop > threshold (latest 2 months in period)
        if (growthRes.status === "fulfilled" && growthRes.value.data?.status === 1) {
          const rows = growthRes.value.data.data || []
          if (rows.length >= 2) {
            // Take last 2 chronological rows for the latest year
            const sorted = [...rows].sort((a, b) =>
              a.year !== b.year ? a.year - b.year : a.month_num - b.month_num
            )
            const last = sorted[sorted.length - 1]
            const prev = sorted[sorted.length - 2]
            const lastRev = parseFloat(last.total_amount || 0)
            const prevRev = parseFloat(prev.total_amount || 0)
            if (prevRev > 0) {
              const change = ((lastRev - prevRev) / prevRev) * 100
              if (change <= -REVENUE_DROP_THRESHOLD) {
                triggered.push({
                  icon: TriangleDownIcon,
                  label: "Revenue drop",
                  value: `${change.toFixed(1)}% MoM`,
                  detail: `${last.month_name} ${last.year} vs ${prev.month_name} ${prev.year}`,
                  severity: "danger",
                  key: "revenue-drop",
                })
              } else if (change >= REVENUE_DROP_THRESHOLD) {
                triggered.push({
                  icon: TriangleUpIcon,
                  label: "Revenue growth",
                  value: `+${change.toFixed(1)}% MoM`,
                  detail: `${last.month_name} ${last.year} vs ${prev.month_name} ${prev.year}`,
                  severity: "good",
                  key: "revenue-growth",
                })
              }
            }
          }
        }

        // Alert: PNP non-active SOH
        if (isPnp && statusRes && statusRes.status === "fulfilled" && statusRes.value.data?.status === 1) {
          const detail = statusRes.value.data.data?.detail || []
          if (detail.length > 0) {
            const totalNonActiveValue = detail.reduce(
              (sum, r) => sum + (parseFloat(r.total_soh_value) || 0),
              0
            )
            triggered.push({
              icon: InfoIcon,
              label: "Non-active SOH (PNP)",
              value: `${detail.length} articles · ${formatCurrency(totalNonActiveValue)}`,
              detail: "Discontinued/delisted articles with stock in stores — clearance candidates",
              severity: "warn",
              key: "non-active-soh",
            })
          }
        }

        setAlerts(triggered)
      } catch (err) {
        if (!axios.isCancel(err)) console.error("AlertPanel fetch error:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchAndCompute()
    return () => controller.abort()
  }, [supplier, retailer, startDate, endDate])

  if (loading) {
    return (
      <Box sx={{ ...neumorph.raised, p: 4, mb: 4 }}>
        <Flex align="center" gap={2}>
          <Spinner size="sm" color={neumorph.accentTeal} />
          <Text fontSize="sm" color={neumorph.textMuted}>
            Checking for alerts…
          </Text>
        </Flex>
      </Box>
    )
  }

  if (alerts.length === 0) {
    return (
      <Box sx={{ ...neumorph.raised, p: 4, mb: 4, borderLeft: `4px solid ${neumorph.accentGreen}` }}>
        <Flex align="center" gap={2}>
          <Icon as={InfoIcon} color={neumorph.accentGreen} />
          <Text fontSize="sm" color={neumorph.textPrimary} fontWeight="600">
            No alerts — everything looks healthy for this supplier/retailer.
          </Text>
        </Flex>
      </Box>
    )
  }

  return (
    <Box sx={{ ...neumorph.raised, p: 4, mb: 4 }}>
      <Flex align="center" justify="space-between" mb={3}>
        <Text fontSize="sm" fontWeight="700" color={neumorph.textPrimary} textTransform="uppercase" letterSpacing="0.05em">
          ⚠ Items needing attention
        </Text>
        <Text fontSize="2xs" color={neumorph.textMuted}>
          {alerts.length} alert{alerts.length !== 1 ? "s" : ""}
        </Text>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 2, lg: alerts.length > 2 ? 4 : alerts.length }} spacing={3}>
        {alerts.map((a) => (
          <AlertItem
            key={a.key}
            icon={a.icon}
            label={a.label}
            value={a.value}
            detail={a.detail}
            severity={a.severity}
          />
        ))}
      </SimpleGrid>
    </Box>
  )
}

export default AlertPanel
